import { useSessionMessageApi } from '../../../../utils/actionableNotifications';
import { MiscOptionsError } from '../../../../features/dropdowns/views/MiscOptions';
import { CustomerReference } from '../queries/CustomerReference';
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import dayjs from "dayjs";
import { Drawer, Button, Input, Select, Switch, DatePicker, Empty, Spin, Tooltip, Badge, Checkbox, Tag, Tabs, Popconfirm, Divider, Typography, Modal } from 'antd';
import {
  CarOutlined, PlusOutlined, EnvironmentOutlined, EnvironmentFilled,
  EditOutlined, DeleteOutlined, CalendarOutlined,
  CopyOutlined, SyncOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useMaps } from "../../../../features/maps/viewmodels/MapsProvider";
import { AddressDeliveryEditor } from './AddressDeliveryEditor';
import { AddressPinStatus } from './AddressPinStatus';
import { validPin } from '../../models/customer/addressLocationModel';
import { engineLayoutLabel, useQv4Theme, _fetchVehicleLogos, getVehicleLogoUrl } from "../../helpers";
import { StaffAvatar } from "../../../staff/StaffAvatar";
import { PIPELINE_COLUMNS, URGENCY_CFG, canBringToToday } from "../../pipeline";
import { SendToSuppliersModal } from "../../../../features/supplier-enquiries/views/SendToSuppliersModal";
import { WorkflowTaskDrawer } from "../workflow-drawer/WorkflowTaskDrawer";
import { QueryEditorModal } from "../queries/QueryEditorModal";
import { useAccess } from "../../../../features/access/context/AccessProvider";
import { CommunicationChannelTag, channelKey } from "../shared/CommunicationChannelPicker";
import { useCustomerCommunication } from "../../viewmodels/customer/useCustomerCommunication";
import { useCustomerQueries } from "../../viewmodels/customer/useCustomerQueries";
import { useCustomerVehicles } from "../../viewmodels/customer/useCustomerVehicles";
import { useCustomerDraft } from "../../viewmodels/customer/useCustomerDraft";
import { CustomerVehicleFamilyEditor } from "../shared/CustomerVehicleFamilyEditor";
import { vehicleDisplay, vehicleMakeName } from '../../models/shared/customerVehiclesV1Model';
import { useVehicleDropdownOptions } from "../../viewmodels/shared/useVehicleDropdownOptions";
import { dropdownOptions } from "../../models/shared/dropdownValuesModel";
import { filterProfileVehicles, filterProfileAddresses, duplicateVinVehicle, DUPLICATE_VIN_MESSAGE } from "../../models/customer/customerProfileListModel";
import { useProfileListSearch } from "../../viewmodels/customer/useProfileListSearch";
import { ProfileListSearch } from "./ProfileListSearch";
import { CustomerProfileState } from './CustomerProfileState';

// The logo fills the card's left column without determining its row height.
function VehicleBrandMark({ brand, colors, children }) {
  const [logoUrl, setLogoUrl] = useState(() => getVehicleLogoUrl(brand));
  useEffect(() => {
    setLogoUrl(getVehicleLogoUrl(brand));
    if (!brand || getVehicleLogoUrl(brand)) return undefined;
    let alive = true;
    _fetchVehicleLogos().then(() => { if (alive) setLogoUrl(getVehicleLogoUrl(brand)); });
    return () => { alive = false; };
  }, [brand]);
  return <>
    <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 48, color: colors.textSub }}>
      <Tooltip title={brand || "Vehicle"}>
        {logoUrl ? <img src={logoUrl} alt={brand} loading="lazy" decoding="async" onError={() => setLogoUrl(null)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
          : <CarOutlined aria-label={brand || "Vehicle"} style={{ fontSize: 32 }} />}
      </Tooltip>
    </div>
    <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
      {!logoUrl && brand && <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, marginBottom: 2 }}>{brand}</div>}
      {children}
    </div>
  </>;
}

const getStatusDisplay = (status) => {
  const col = PIPELINE_COLUMNS.find(c => !c.isChat && c.statuses?.includes(status));
  return col ? { label: col.label, color: col.color } : { label: status, color: "#8c8c8c" };
};

// Cycled by option position — types are DB-driven now, so there's no fixed
// count to hardcode colors for.
const TYPE_LABEL_COLORS = ["#1677ff", "#722ed1", "#fa8c16", "#13a8a8", "#eb2f96", "#a0d911", "#faad14", "#2f54eb"];

export const CustomerEditDrawer = forwardRef(function CustomerEditDrawer({ customer, conversationId, profileState, onRetryProfile, open, onClose, onSaved, defaultTab = "customer", defaultCreateOpen = false, imageMessages = [], locationMessages = [], imageMessagesLoading = false, onQueryCreated, nudgeTypeIfUnset = false, onFieldChanged, closeOnTypeSaved = false, onDirtyChange, placement = "right" }, ref) {
  const message = useSessionMessageApi();
  const { can } = useAccess();
  const canCreateQuery = can("customers.create_query") && can("sales_pipeline.create_query");
  const canChangeQueryStatus = can("sales_pipeline.change_status");
  const canManageVehicles = can("customers.manage_vehicles");
  const { t, isDark } = useQv4Theme();
  const technical = useVehicleDropdownOptions(open && canManageVehicles);

  // tab state
  const [activeTab, setActiveTab] = useState(["customer", "vehicles", "addresses", "queries"].includes(defaultTab) ? defaultTab : "customer");
  const setActiveSection = setActiveTab;
  const vehicleSearch = useProfileListSearch({ enabled: open, active: activeTab === "vehicles", customerId: customer?.customer_id });
  const addressSearch = useProfileListSearch({ enabled: open, active: activeTab === "addresses", customerId: customer?.customer_id });
  // Consumed once per open — without this, any API call made while the
  // drawer is open (e.g. saving a field on the customer tab) refreshes the
  // `customer` prop, which re-runs the effect below and re-applies
  // defaultTab, yanking the user back to it mid-edit. Reset only when the
  // drawer actually (re)opens, not on every customer data refresh.
  const appliedDefaultTabRef = useRef(false);

  // queries tab state
  const { queriesList, queriesLoading, legacyQueries, confirmBringTodayPk, setConfirmBringTodayPk, loadQueries, handleBringToToday } = useCustomerQueries({ customer, open, activeTab, message });
  // Read-only "what have suppliers quoted for this before" reference,
  // purely informational at this pre-creation stage — see
  // docs/superpowers/specs/2026-08-22-supplier-price-history-design.md.
  const [selectedQueryPk, setSelectedQueryPk] = useState(null);
  const [sendQueryPk, setSendQueryPk] = useState(null);
  const handleQueryCreated = (card, openSend) => {
    if (onQueryCreated) onQueryCreated(card, openSend);
    else if (openSend) setSendQueryPk(card.query_pk ?? card.task_id ?? card.id);
  };

  const subFormRef = useRef(null);
  const [addressCancelOpen, setAddressCancelOpen] = useState(false);
  const vehicleSubFormRef = useRef(null);

  // Opt-in (ChatPanelHeader's avatar-opened flow uses this; other openers of
  // this same drawer — e.g. Operations/QuerysetV4's own usages — don't pass
  // it, so they're unaffected) — if the customer's type is still unset when
  // this drawer opens, scroll to and open the Type select below, plus a
  // one-off toast, instead of leaving staff to notice the empty field
  // themselves buried in this form.
  const [typeNudgeOpen, setTypeNudgeOpen] = useState(false);
  const [typeNudgePulsing, setTypeNudgePulsing] = useState(false);
  const typeFieldRef = useRef(null);
  const { communicationChannels, customerCommunicationDefault, communicationLoading, communicationSaving, saveCustomerCommunicationDefault } = useCustomerCommunication({ customer, open, message });
  const { vehicleSubForm, setVehicleSubForm, familyVehicleEditor, setFamilyVehicleEditor, vehicles, brandOptions, modelOptions, setModelOptions,
    loadingBrands, loadingModels, vehiclesLoading, vehiclesSaving, editingVehicleId, setEditingVehicleId, regionOptions, fuelTypeOptions,
    loadVehicleModels, openAddVehicleForm, routeLegacyVehicle, saveVehicleForm, editVehicle, handleDeleteVehicle, onFamilySaved,
  } = useCustomerVehicles({ customer, open, setActiveSection, queriesList, loadQueries, onFieldChanged, message });
  const [createFormOpen, setCreateFormOpen] = useState(false);
  useEffect(() => { setCreateFormOpen(open && defaultCreateOpen); }, [open, defaultCreateOpen, customer?.customer_id]);
  const { draft, setField, addresses, sourceOptions, typeOptions, typeOptionsState, subForm, setSubForm, emailError, setEmailError,
    saving, addressSaving, addressError, deletingAddressId, editAddress, handleDeleteAddress, saveSubForm,
    operationsImpact, operationsReason, setOperationsReason, confirmOperationsImpact, cancelOperationsImpact,
    saveCustomer, topLevelDirty, sourceSaving, handleSourceChange, typeSaving, typeError, retryType, handleTypeChange, copyingWatiName, copyWatiName,
  } = useCustomerDraft({ customer, conversationId, onReloadProfile: onRetryProfile, open, onSaved, onFieldChanged, closeOnTypeSaved, onClose, onTypeStart: () => setTypeNudgeOpen(false), message });

  const { isLoaded: isMapsLoaded } = useMaps();

  // rc-drawer's own Escape handling lives on a keydown listener on the
  // drawer's own panel element, so it only fires if the keydown bubbles up
  // to that panel uninterrupted. This drawer is the most Select/AutoComplete
  // -heavy form in the app (vehicle brand/model, engine layout, transmission,
  // drivetrain, fuel type, region, address type picker, the Google Places
  // Autocomplete...), and several of those swallow Escape themselves
  // (stopPropagation, closing their own popup or just blurring) regardless
  // of whether their own popup is even open — so a stray Escape while focus
  // is inside any of them never reaches the panel. A capture-phase listener
  // on the document fires before any of that, so it isn't at the mercy of
  // which control last had focus.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;
      // This drawer can itself have a SendToSuppliersModal or a nested
      // WorkflowTaskDrawer open on top of it (sendQueryPk / selectedQueryPk
      // below) — those already close correctly on Escape on their own, via
      // the normal bubble-phase handling this capture-phase listener would
      // otherwise pre-empt. Step aside and let the topmost one handle it.
      if (createFormOpen || sendQueryPk || selectedQueryPk || familyVehicleEditor || addressCancelOpen || operationsImpact) return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose, createFormOpen, sendQueryPk, selectedQueryPk, familyVehicleEditor, addressCancelOpen, operationsImpact]);

  useEffect(() => {
    if (!open) appliedDefaultTabRef.current = false;
    if (!open || !customer) return;
    if (!appliedDefaultTabRef.current) {
      // Guard against unknown tab keys — an unmatched activeKey renders the
      // Tabs with nothing selected and hides the Save footer
      setActiveTab(["customer", "vehicles", "addresses", "queries"].includes(defaultTab) ? defaultTab : "customer");
      appliedDefaultTabRef.current = true;
    }
  }, [open, customer]); // eslint-disable-line

  useEffect(() => {
    if (!open || !customer || !nudgeTypeIfUnset || customer.type != null) {
      setTypeNudgeOpen(false);
      setTypeNudgePulsing(false);
      return;
    }
    // Give the drawer's own slide-in transition a moment to finish before
    // scrolling — doing it immediately can fight the transition and land in
    // the wrong spot.
    const openTimer = setTimeout(() => {
      typeFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTypeNudgeOpen(true);
      setTypeNudgePulsing(true);
    }, 350);
    const pulseTimer = setTimeout(() => setTypeNudgePulsing(false), 4000);
    return () => { clearTimeout(openTimer); clearTimeout(pulseTimer); };
  }, [open, customer, nudgeTypeIfUnset]); // eslint-disable-line

  const openAddSubForm = () => {
    setActiveSection("addresses");
    setSubForm({
      name: customer?.customer_real_name?.trim() || customer?.customer_name?.trim() || "",
      number: customer?.customer_mobile || "",
      address: "",
      type: "Home",
      delivery_mode: 'own',
      pref_time: null,
    });
    setTimeout(() => subFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const handleSave = async () => {
    const result = await saveCustomer();
    if (result !== "error") onClose();
  };

  // Unsaved-changes signal for the chat this profile was opened from:
  // closing that chat, or switching to another, would drop whatever is
  // mid-entry here. Reported up so ChatConversation's leave guard can ask
  // first instead. Any open vehicle/address subform or new-query form
  // counts as dirty regardless of what's been typed into it — it can't be
  // saved automatically (unvalidated), so the safest default is to always
  // warn rather than risk silently losing typed-but-unsubmitted entries.
  const hasUnsavedChanges = open && !!customer?.customer_id &&
    (topLevelDirty || subForm !== null || vehicleSubForm !== null || createFormOpen);
  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges]); // eslint-disable-line
  useEffect(() => () => onDirtyChange?.(false), []); // eslint-disable-line

  useImperativeHandle(ref, () => ({ saveCustomer }));

  const customerDataTab = (
    <>
      <div style={{ display: "grid", gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Customer Name</div>
          {/* The name the customer set on their WhatsApp account. Read-only:
              staff put the name they know in Real Name, which wins everywhere. */}
          <Tooltip title="Set by the customer on WhatsApp. Use Real Name to show a different name." placement="topLeft">
            <Input value={draft.customer_name || ""} readOnly variant="filled" placeholder="No WhatsApp name" aria-label="Customer Name" />
          </Tooltip>
        </div>
        <div>
          <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
            Real Name
            {!!draft.customer_name?.trim() && (
              <Tooltip title="Copy WhatsApp name into this field">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  loading={copyingWatiName}
                  onClick={copyWatiName}
                  style={{ padding: "0 4px", height: 18, lineHeight: "18px" }}
                />
              </Tooltip>
            )}
          </div>
          <Input
            value={draft.customer_real_name || ""}
            onChange={e => setField("customer_real_name", e.target.value)}
            placeholder="Legal / real name"
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Default communication channel</div>
          {can("customers.edit") ? <Select
            style={{ width: "100%" }}
            value={channelKey(customerCommunicationDefault)}
            placeholder="Not set"
            loading={communicationLoading || communicationSaving}
            disabled={communicationLoading || communicationSaving}
            onChange={saveCustomerCommunicationDefault}
            options={communicationChannels.map(item => ({ value: item.key, label: item.label }))}
          /> : <CommunicationChannelTag selection={customerCommunicationDefault} showMode />}
          <div style={{ fontSize: 11, color: t.textSub, marginTop: 4 }}>Used only to preselect the channel for future queries. Existing queries are unchanged.</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Email</div>
          <Input
            value={draft.customer_email || ""}
            onChange={e => {
              const v = e.target.value;
              setField("customer_email", v);
              if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) setEmailError("Invalid email address");
              else setEmailError("");
            }}
            placeholder="Email address"
            status={emailError ? "error" : ""}
          />
          {emailError && <div style={{ fontSize: 11, color: "#ff4d4f", marginTop: 2 }}>{emailError}</div>}
        </div>
        <div>
          <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Phone</div>
          {/* Historically read-only — WATI guaranteed a phone number always
              existed for every contact. Now some WATI contacts hide it
              entirely (username-only), so this needs to be a normal editable
              field like Real Name/Email above, not a one-time "add" button
              that locks afterward — typos need fixing regardless of whether
              the number started empty. Empty state gets a distinct
              "+ Add phone number" prompt so staff notice the gap; both cases
              save through the same path. */}
          <Input
            value={draft.customer_mobile || ""}
            onChange={e => setField("customer_mobile", e.target.value)}
            placeholder="+ Add phone number"
            prefix={!draft.customer_mobile ? <PlusOutlined style={{ color: t.textSub, fontSize: 11 }} /> : undefined}
          />
          {!!draft.customer_mobile && (
            <div style={{ fontSize: 12, color: t.textSub, padding: "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
              {(customer?.country || customer?.customer_country) && (
                <span>{customer?.country || customer?.customer_country}</span>
              )}
              <Tooltip title="Copy phone number">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(draft.customer_mobile);
                    message.success({ content: "Copied", duration: 1.5 });
                  }}
                  style={{ padding: "0 4px", height: 18, lineHeight: "18px" }}
                />
              </Tooltip>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Source</div>
            <Select
              style={{ width: "100%" }}
              value={draft.source ?? null}
              onChange={handleSourceChange}
              loading={sourceSaving}
              disabled={sourceSaving}
              allowClear
              placeholder="Select source"
              options={sourceOptions.map(o => ({ value: o.value, label: o.label }))}
            />
          </div>
          <div ref={typeFieldRef} style={{ borderRadius: 6, animation: typeNudgePulsing ? "pulse 1s ease-in-out infinite" : "none" }}>
            <div style={{ fontSize: 12, color: t.textSub, marginBottom: 4 }}>Type</div>
            <Select
              style={{ width: 110 }}
              value={draft.type ?? null}
              onChange={handleTypeChange}
              open={typeNudgeOpen || undefined}
              onOpenChange={setTypeNudgeOpen}
              loading={typeSaving}
              disabled={typeSaving || typeOptionsState?.loading || Boolean(typeOptionsState?.error)}
              allowClear
              placeholder="Type?"
              options={typeOptions.map((o, i) => ({
                value: o.value,
                disabled: o.disabled,
                label: <span style={{ color: TYPE_LABEL_COLORS[i % TYPE_LABEL_COLORS.length], fontWeight: 600 }}>{o.label}</span>,
              }))}
            />
            <MiscOptionsError state={typeOptionsState || {}} />
          </div>
        </div>
      </div>

    </>
  );

  const visibleVehicles = filterProfileVehicles(vehicles, vehicleSearch.query, brandOptions);
  const visibleAddresses = filterProfileAddresses(addresses, addressSearch.query);
  const duplicateLegacyVin = duplicateVinVehicle(vehicles, vehicleSubForm?.vin, editingVehicleId);
  const vehicleAndAddressTabs = [
          {
            key: "vehicles",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 0 }}>
                Vehicles
                {vehicles.length > 0 && (
                  <Badge count={vehicles.length} size="small" color={activeTab === "vehicles" ? "var(--app-color-accent)" : "#8c8c8c"} />
                )}
              </span>
            ),
            children: (
              <>
                <ProfileListSearch search={vehicleSearch} label="Search vehicles" colors={t}>
                  {canManageVehicles && <Tooltip title={vehicleSearch.expanded ? "Add vehicle" : undefined}><Button aria-label="Add vehicle" icon={<CarOutlined />} onClick={openAddVehicleForm}>{!vehicleSearch.expanded && "Add vehicle"}</Button></Tooltip>}
                </ProfileListSearch>
                {vehicleSubForm !== null && (
                  <div ref={vehicleSubFormRef}
                    style={{ border: `1px solid ${editingVehicleId ? "#d46b08" : "var(--app-color-accent)"}`, borderRadius: 6, padding: 12, background: editingVehicleId ? (isDark ? "rgba(250,173,20,0.12)" : "#fff7e6") : (isDark ? "color-mix(in srgb, var(--app-color-accent) 12%, transparent)" : "var(--app-color-accent-bg)"), marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: editingVehicleId ? "#d46b08" : "var(--app-color-accent)", marginBottom: 8 }}>
                      {editingVehicleId ? "Edit Vehicle" : "Add New Vehicle"}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px" }}>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Make</div>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          loading={loadingBrands}
                          placeholder="Select brand"
                          filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                          options={brandOptions}
                          value={vehicleSubForm.brand_id ?? null}
                          onChange={val => {
                            setVehicleSubForm(f => ({ ...f, brand_id: val ?? null, model_id: null }));
                            loadVehicleModels(val);
                          }}
                          allowClear
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Model</div>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          loading={loadingModels}
                          placeholder={vehicleSubForm.brand_id ? "Select model" : "Select brand first"}
                          disabled={!vehicleSubForm.brand_id}
                          filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                          options={modelOptions}
                          value={vehicleSubForm.model_id ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, model_id: val ?? null }))}
                          allowClear
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Variant</div>
                        <Input
                          placeholder="e.g. Sport, LX, SE"
                          value={vehicleSubForm.variant}
                          onChange={e => setVehicleSubForm(f => ({ ...f, variant: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Year</div>
                        <DatePicker
                          style={{ width: "100%" }}
                          picker="year"
                          placeholder="Select year"
                          value={vehicleSubForm.year ? dayjs(vehicleSubForm.year, "YYYY") : null}
                          onChange={(_, str) => setVehicleSubForm(f => ({ ...f, year: str || null }))}
                          disabledDate={d => d && (d.year() <= 2000 || d.year() > dayjs().year())}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>VIN / Chassis No.</div>
                        <Input
                          placeholder="e.g. 1HGCM82633A004352"
                          value={vehicleSubForm.vin}
                          onChange={e => setVehicleSubForm(f => ({ ...f, vin: e.target.value }))}
                          status={duplicateLegacyVin ? "error" : undefined}
                        />
                        {duplicateLegacyVin && <Typography.Text type="danger" role="alert">{DUPLICATE_VIN_MESSAGE}</Typography.Text>}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Engine Code</div>
                        <Input
                          placeholder="e.g. 2AR-FE"
                          value={vehicleSubForm.engine_code}
                          onChange={e => setVehicleSubForm(f => ({ ...f, engine_code: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Engine Displacement (L)</div>
                        <Input
                          placeholder="e.g. 2.0"
                          value={vehicleSubForm.engine_displacement}
                          onChange={e => {
                            const val = e.target.value;
                            if (val === "" || /^\d{0,2}(\.\d{0,1})?$/.test(val)) {
                              setVehicleSubForm(f => ({ ...f, engine_displacement: val }));
                            }
                          }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Engine Layout</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select layout"
                          allowClear
                          value={vehicleSubForm.engine_layout ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, engine_layout: val ?? null }))}
                          options={dropdownOptions(technical.rows.engine_layout || [], vehicleSubForm.engine_layout)}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Transmission Type</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select type"
                          allowClear
                          value={vehicleSubForm.transmission_type ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, transmission_type: val ?? null }))}
                          options={dropdownOptions(technical.rows.transmission_type || [], vehicleSubForm.transmission_type)}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Transmission Code</div>
                        <Input
                          placeholder="e.g. A750E"
                          value={vehicleSubForm.transmission_code}
                          onChange={e => setVehicleSubForm(f => ({ ...f, transmission_code: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Drivetrain</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select drivetrain"
                          allowClear
                          value={vehicleSubForm.drivetrain ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, drivetrain: val ?? null }))}
                          options={dropdownOptions(technical.rows.drivetrain || [], vehicleSubForm.drivetrain)}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Fuel Type</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select fuel type"
                          allowClear
                          value={vehicleSubForm.fuel_type ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, fuel_type: val ?? null }))}
                          options={fuelTypeOptions}
                          showSearch
                          optionFilterProp="label"
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Country Spec</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select spec"
                          allowClear
                          value={vehicleSubForm.country_spec ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, country_spec: val ?? null }))}
                          options={regionOptions}
                          showSearch
                          optionFilterProp="label"
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Trim Info</div>
                        <Input
                          placeholder="e.g. Sport Edition, Night Package"
                          value={vehicleSubForm.trim_info}
                          onChange={e => setVehicleSubForm(f => ({ ...f, trim_info: e.target.value }))}
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: 11, color: t.textSub, marginBottom: 3 }}>Additional Notes</div>
                        <Input.TextArea
                          rows={3}
                          placeholder="Any extra details about the vehicle..."
                          value={vehicleSubForm.notes}
                          onChange={e => setVehicleSubForm(f => ({ ...f, notes: e.target.value }))}
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Checkbox
                          checked={vehicleSubForm.is_primary}
                          onChange={e => setVehicleSubForm(f => ({ ...f, is_primary: e.target.checked }))}
                        >
                          <span style={{ fontSize: 12 }}>Primary vehicle</span>
                        </Checkbox>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12, color: t.textSub }}>Active</span>
                          <Switch
                            size="small"
                            checked={vehicleSubForm.status}
                            onChange={val => setVehicleSubForm(f => ({ ...f, status: val }))}
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                      <Button size="small" onClick={() => { setVehicleSubForm(null); setEditingVehicleId(null); setModelOptions([]); }}>Cancel</Button>
                      <Button size="small" type="primary" loading={vehiclesSaving}
                        disabled={vehiclesLoading || Boolean(duplicateLegacyVin)}
                        style={editingVehicleId ? { background: "#d46b08", borderColor: "#d46b08" } : {}}
                        onClick={saveVehicleForm}>
                        {editingVehicleId ? "Save Changes" : "Add Vehicle"}
                      </Button>
                    </div>
                  </div>
                )}
                {open && familyVehicleEditor && !familyVehicleEditor.id && canManageVehicles && <CustomerVehicleFamilyEditor inline
                  existingVehicles={vehicles} existingVehiclesLoading={vehiclesLoading}
                  technicalOptions={technical}
                  customerId={customer?.customer_id} onClose={() => setFamilyVehicleEditor(null)} onLegacy={routeLegacyVehicle}
                  onSaved={onFamilySaved}
                />}
                {vehiclesLoading
                  ? <div style={{ textAlign: "center", padding: "16px 0" }}><Spin size="small" /></div>
                  : visibleVehicles.length === 0
                    ? <Empty description={vehicleSearch.query.trim() ? "No matching vehicles" : "No vehicles yet"} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    : visibleVehicles.map(v => {
                      const brandLabel = vehicleMakeName(v) || (brandOptions.find(b => b.value === v.brand)?.label) || '—';
                      const modelLabel = vehicleDisplay(v, { includeMake: false }) || '—';
                      const isPrimary = v.is_primary_vehicle === 1 || v.is_primary === true;
                      const isActive = v.status !== 0 && v.status !== false;
                      return (
                        <div key={v.id}
                          style={{
                            border: `1px solid ${t.border}`, borderRadius: 6, padding: "8px 10px",
                            marginBottom: 6, display: "grid", gridTemplateColumns: "64px minmax(0, 1fr)", gap: 12
                          }}>
                          <VehicleBrandMark brand={brandLabel === "—" ? null : brandLabel} colors={t}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: t.text }}>
                              {modelLabel}
                              {isPrimary && <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>Primary</Tag>}
                              {!isActive && <Tag color="default" style={{ marginLeft: 4, fontSize: 10 }}>Inactive</Tag>}
                            </div>
                          {canManageVehicles && <div style={{ display: "flex", flexShrink: 0, gap: 2 }}>
                            <Tooltip title="Edit vehicle">
                              <Button type="text" size="small" icon={<EditOutlined />}
                                style={{ color: "var(--app-color-accent)" }}
                                onClick={() => {
                                  if (editVehicle(v)) setTimeout(() => vehicleSubFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                                }}
                              />
                            </Tooltip>
                            <Button type="text" size="small" danger icon={<DeleteOutlined />}
                              onClick={() => handleDeleteVehicle(v)} />
                          </div>}
                            </div>
                            {v.vin && <div style={{ fontSize: 11, color: t.textSub }}>VIN: {v.vin}</div>}
                            {v.engine_displacement && <div style={{ fontSize: 11, color: t.textSub }}>{v.engine_displacement}L{v.engine_layout ? ` · ${engineLayoutLabel(v.engine_layout)}` : ""}{v.fuel_type ? ` · ${v.fuel_type}` : ""}</div>}
                          </VehicleBrandMark>
                        </div>
                      );
                    })
                }
              </>
            ),
          },

          {
            key: "addresses",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 1 }}>
                Addresses
                {addresses.length > 0 && (
                  <Badge count={addresses.length} size="small" color={activeTab === "addresses" ? "var(--app-color-accent)" : "#8c8c8c"} />
                )}
              </span>
            ),
            children: (
              <>
                <ProfileListSearch search={addressSearch} label="Search addresses" colors={t}>
                  <Tooltip title={addressSearch.expanded ? "Add address" : undefined}><Button aria-label="Add address" icon={<EnvironmentOutlined />} onClick={openAddSubForm} disabled={addressSaving}>{!addressSearch.expanded && "Add address"}</Button></Tooltip>
                </ProfileListSearch>
                {subForm !== null && (
                  <div ref={subFormRef}
                    style={{
                      border: "1px solid var(--app-color-accent)", borderRadius: 6, padding: 12, background: isDark ? "color-mix(in srgb, var(--app-color-accent) 12%, transparent)" : "var(--app-color-accent-bg)",
                      display: "grid", gap: 8, marginBottom: 12
                    }}>
                    <AddressDeliveryEditor key={subForm.id || 'new'} form={subForm} isLoaded={isMapsLoaded} isDark={isDark} locationMessages={locationMessages}
                      onChange={patch => setSubForm(f => ({ ...f, ...patch }))} onSave={() => saveSubForm()} onCancel={() => setAddressCancelOpen(true)}
                      saving={addressSaving} addressError={addressError} operationsImpact={operationsImpact} operationsReason={operationsReason}
                      setOperationsReason={setOperationsReason} confirmOperationsImpact={confirmOperationsImpact} cancelOperationsImpact={cancelOperationsImpact} />
                    <Modal title="Discard unsaved address changes?" open={addressCancelOpen} centered okText="Discard" okButtonProps={{ danger: true }}
                      onCancel={() => setAddressCancelOpen(false)} onOk={() => { setAddressCancelOpen(false); setSubForm(null); cancelOperationsImpact(); }}>
                      The address changes have not been saved.
                    </Modal>
                  </div>
                )}

                <div style={{ display: "grid", gap: 8 }}>
                  {addresses.length === 0 && (
                    <Empty description="No addresses" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                  {addresses.length > 0 && !visibleAddresses.length && <Empty description="No matching addresses" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                  {visibleAddresses.filter(addr => subForm?.id !== addr.id).map(addr => (
                    <div key={addr.id}
                      style={{ border: `1px solid ${t.border}`, borderRadius: 6, padding: "8px 12px", background: t.headerBg }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <span style={{ fontSize: 11, background: t.hover, color: t.textSub, padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>
                          {addr.type}
                        </span>
                        <div style={{ display: "flex", gap: 4 }}>
                          <Button type="text" size="small" disabled={addressSaving || deletingAddressId != null} icon={<EditOutlined />} onClick={() => editAddress(addr)} />
                          <Popconfirm
                            title="Delete this address?"
                            description="This action cannot be undone."
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDeleteAddress(addr.id)}
                          >
                            <Button type="text" size="small" danger loading={deletingAddressId === addr.id} disabled={addressSaving || (deletingAddressId != null && deletingAddressId !== addr.id)} icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, marginTop: 4 }}>
                        {addr.name}
                        <span style={{ fontWeight: 400, color: t.textSub, fontSize: 12, marginLeft: 8 }}>{addr.number}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, fontSize: 12, color: t.textSub, marginTop: 2 }}>
                        <Tooltip title={validPin(addr.latitude, addr.longitude) ? "Location pin added" : "No location pin"}>
                          <span role="img" aria-label={validPin(addr.latitude, addr.longitude) ? "Location pin added" : "No location pin"} style={{ flexShrink: 0, opacity: validPin(addr.latitude, addr.longitude) ? 1 : 0.35 }}>
                            {validPin(addr.latitude, addr.longitude) ? <EnvironmentFilled /> : <EnvironmentOutlined />}
                          </span>
                        </Tooltip>
                        <span>{addr.address}</span>
                      </div>
                      {!validPin(addr.latitude, addr.longitude) && <><AddressPinStatus address={addr} /><Button size="small" onClick={() => editAddress(addr)}>Set pin</Button></>}
                      {!addr.address_format && addr.delivery_mode === 'courier' && <Tag style={{ opacity: 0.65 }}>Courier (old)</Tag>}
                      {!addr.address_format && <Button type="link" size="small" onClick={() => editAddress(addr)}>Old format, edit to complete</Button>}
                      {addr.details && <div>{addr.details}</div>}
                      {addr.pref_time && Array.isArray(addr.pref_time) && addr.pref_time.length === 2 && (
                        <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>
                          {dayjs(addr.pref_time[0]).format("hh:mm A")} – {dayjs(addr.pref_time[1]).format("hh:mm A")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ),
          },
        ].filter(Boolean);

  const queriesTab = (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Create form toggle */}
      {canCreateQuery && (<><Button type="dashed" icon={<PlusOutlined />} onClick={() => setCreateFormOpen(true)} block>New Query</Button><QueryEditorModal open={createFormOpen} customer={customer} imageMessages={imageMessages} onClose={() => setCreateFormOpen(false)} onReload={loadQueries} onQueryCreated={handleQueryCreated} /></>)}
      {/* Query list */}
      {queriesLoading ? (
        <div style={{ textAlign: "center", padding: "32px 0" }}><Spin /></div>
      ) : queriesList.length === 0 ? (
        <Empty description="No queries yet" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 24 }} />
      ) : (
        queriesList.map(q => {
          const st = getStatusDisplay(q.status);
          const v = q.vehicle;
          const vehicleLabel = v
            ? vehicleDisplay(v)
            : null;
          const urgCfg = q.urgency ? URGENCY_CFG[q.urgency] : null;
          const assignee = q.assigned_user;
          const assigneeName = assignee
            ? (assignee.name ? `${assignee.name}${assignee.surname ? " " + assignee.surname : ""}` : assignee.username)
            : null;

          return (
            <div
              key={q.query_pk}
              onClick={() => setSelectedQueryPk(q.query_pk)}
              style={{
                border: `1px solid ${t.border}`,
                borderLeft: `4px solid ${st.color}`,
                borderRadius: 6,
                padding: "10px 12px",
                cursor: "pointer",
                background: t.cardBg,
                transition: "box-shadow 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.10)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, color: "var(--app-color-accent)", fontSize: 13 }}>#{q.query_id}</span><CustomerReference value={q.customer_reference} />
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  {urgCfg && (
                    <span style={{
                      padding: "1px 6px", borderRadius: 20, fontSize: 10, fontWeight: 600,
                      background: urgCfg.pill, color: urgCfg.color, border: `1px solid ${urgCfg.color}`,
                    }}>
                      {urgCfg.label.split(" · ")[1]}
                    </span>
                  )}
                  <Tag style={{ margin: 0, fontSize: 10, padding: "0 5px", color: st.color, borderColor: st.color, background: `${st.color}18` }}>
                    {st.label}
                  </Tag>
                  {canChangeQueryStatus && canBringToToday(q) && (
                    <Popconfirm
                      title="Bring this query to today?"
                      okText="Yes"
                      cancelText="No"
                      open={confirmBringTodayPk === q.query_pk}
                      onConfirm={e => { e?.stopPropagation(); handleBringToToday(q.query_pk); }}
                      onCancel={e => { e?.stopPropagation(); setConfirmBringTodayPk(null); }}
                      onOpenChange={open => { if (!open) setConfirmBringTodayPk(null); }}
                    >
                      <Tooltip title="Bring to today">
                        <Button
                          aria-label="Bring to today"
                          type="text"
                          size="small"
                          icon={<SyncOutlined style={{ fontSize: 11 }} />}
                          onClick={e => { e.stopPropagation(); setConfirmBringTodayPk(q.query_pk); }}
                          style={{ padding: "0 4px", height: 18 }}
                        />
                      </Tooltip>
                    </Popconfirm>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {vehicleLabel && (
                  <span style={{ fontSize: 12, color: t.text, display: "flex", alignItems: "center", gap: 4 }}>
                    <CarOutlined style={{ fontSize: 11, color: t.textSub }} />
                    {vehicleLabel}
                  </span>
                )}
                {assigneeName && (
                  <span style={{ fontSize: 11, color: t.textSub, display: "flex", alignItems: "center", gap: 4 }}>
                    <StaffAvatar size={14} userId={assignee?.id ?? q.assigned_to} name={assigneeName} />
                    {assigneeName}
                  </span>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
                {q.parts_count > 0 && (
                  <span style={{ fontSize: 11, color: t.textSub }}>
                    {q.parts_count} {q.parts_count === 1 ? "part" : "parts"}
                  </span>
                )}
                {q.created_at && (
                  <span style={{ fontSize: 11, color: t.textSub, display: "flex", alignItems: "center", gap: 3 }}>
                    <CalendarOutlined style={{ fontSize: 10 }} />
                    {dayjs(q.created_at).format("DD MMM YYYY")}
                  </span>
                )}
              </div>
            </div>
          );
        })
      )}
      {legacyQueries.length > 0 && (
        <>
          <Divider style={{ margin: "12px 0 8px", fontSize: 11, color: t.textSub }}>
            Archived queries ({legacyQueries.length})
          </Divider>
          {legacyQueries.map(lq => (
            <div
              key={lq.query_id}
              style={{
                border: `1px solid ${t.border}`,
                borderRadius: 6,
                padding: "8px 12px",
                marginBottom: 6,
                background: t.headerBg,
                opacity: 0.65,
                cursor: "default",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 2 }}>
                <span style={{ fontWeight: 700, color: t.textSub, fontSize: 12 }}>#{lq.query_id}</span>
                <Tag color="default" style={{ margin: 0, fontSize: 9 }}>Archived</Tag>
              </div>
              <div style={{ fontSize: 11, color: t.textSub }}>
                {[lq.make, lq.model, lq.body_part].filter(Boolean).join(" · ")}
                {lq.date && ` · ${lq.date}`}
                {lq.status && ` · ${lq.status}`}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );

  return (
    <>
      <Drawer
        placement={placement}
        width={485}
        open={open}
        onClose={onClose}
        destroyOnHidden={false}
        mask={false}
        maskClosable={false}
        push={false}
        closable={false}
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700 }}>Customer</div>
              <div style={{ fontSize: 12, color: t.textSub, fontWeight: 400 }}>
                {customer?.customer_name || customer?.customer_mobile || ""}
              </div>
            </div>
            <Tooltip title="Close" getPopupContainer={n => n.parentElement ?? document.body}>
              <Button type="text" aria-label="Close customer profile" icon={<CloseOutlined />} onClick={onClose} style={{ color: t.textSub }} />
            </Tooltip>
          </div>
        }
        footer={
          activeTab === "customer" ? (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button type="primary" loading={saving} disabled={!topLevelDirty} onClick={handleSave}>Save</Button>
            </div>
          ) : null
        }
        styles={{ body: { padding: 0 } }}
      >
        <CustomerProfileState state={profileState} onRetry={onRetryProfile} />
        <CustomerProfileState state={typeError} onRetry={retryType} />
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          style={{ height: "100%" }}
          tabBarStyle={{ margin: 0, paddingLeft: 16, paddingRight: 16, borderBottom: `1px solid ${t.border}` }}
          items={[
            {
              key: "customer",
              label: "Customer Data",
              children: (
                // No overflowY/height:100% here on purpose — this div's
                // height never resolves against the tabpane's auto-height
                // ancestor, so as a "scroll container" it's inert (never
                // actually overflows). But `position: sticky` still binds
                // to the *nearest* such container regardless of whether it
                // scrolls in practice, which was hijacking the Vehicles/
                // Addresses tab bar's stickiness away from the real
                // scroller — `.ant-drawer-body` (antd's own flex:1,
                // min-height:0, overflow:auto). Letting the body be the
                // only overflow:auto ancestor is what makes that sticky bar
                // actually stick.
                <div style={{ padding: "16px 24px" }}>
                  {customerDataTab}
                </div>
              ),
            },
            ...vehicleAndAddressTabs.map(tab => ({ ...tab, children: <div style={{ padding: "16px 24px" }}>{tab.children}</div> })),
            {
              key: "queries",
              label: (
                <span style={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Queries
                  {queriesList.length > 0 && (
                    <Badge count={queriesList.length} size="small" color="#8c8c8c" style={{ marginLeft: 6 }} />
                  )}
                </span>
              ),
              children: (
                <div style={{ padding: "16px 24px", overflowY: "auto", height: "100%" }}>
                  {queriesTab}
                </div>
              ),
            },
          ]}
        />
      </Drawer>

      <SendToSuppliersModal queryPk={sendQueryPk} open={!!sendQueryPk} onClose={() => setSendQueryPk(null)} />
      <WorkflowTaskDrawer
        queryPk={selectedQueryPk}
        onClose={() => setSelectedQueryPk(null)}
      />
      {open && familyVehicleEditor?.id && canManageVehicles && <CustomerVehicleFamilyEditor
        existingVehicles={vehicles} existingVehiclesLoading={vehiclesLoading}
        technicalOptions={technical}
        id={familyVehicleEditor.id} customerId={customer?.customer_id} onClose={() => setFamilyVehicleEditor(null)} onLegacy={routeLegacyVehicle}
        onSaved={onFamilySaved}
      />}

    </>
  );
});
